/**
 * SeedPrefetchService
 *
 * Progressively loads the split IDB seed shards so each stage of the
 * vehicle-selector flow is covered as early as possible:
 *
 *   App starts          → preloadYearsSeed()    loads idb-seed-years.json  (~5 KB)
 *   App starts (bg)     → preloadMakesSeed()    loads idb-seed-makes.json  (~50 KB)
 *   User picks a year   → preloadModelsForYear() loads idb-seed-models-{year}.json (~200 KB)
 *
 * All fetches are fire-and-forget and never block rendering.
 * IDBCacheService.seedIDBFromStaticData() is idempotent — re-calling with the
 * same generatedAt timestamp is a no-op (guarded by localStorage per shard key).
 *
 * In addition to writing to IDB, the loaded seeds are cached in-memory so
 * callers can read them directly via getSeed*FromMemory(). This is a robust
 * fallback for the offline flow when IDB is empty / unavailable / racy.
 */

import { seedIDBFromStaticData, IDBSeedFile } from "./IDBCacheService";

/** Abort the seed fetch and fall back to offline data if the server doesn't respond within this window. */
const SEED_FETCH_TIMEOUT_MS = 3000;

/** In-flight promises keyed by filename so we never double-fetch the same shard. */
const inFlight = new Map<string, Promise<void>>();

/** In-memory cache of every loaded shard, keyed by store name. */
const memorySeed: Record<string, Record<string, unknown>> = {};

/** Resolved promise of the makes shard load — awaitable by fetchMakes. */
let _makesSeedPromise: Promise<void> = Promise.resolve();
/** Resolved promise per year for models shards — awaitable by fetchModels. */
const _modelsSeedPromises = new Map<string, Promise<void>>();
/** Resolved promise of the years shard load — awaitable by fetchYears. */
let _yearsSeedPromise: Promise<void> = Promise.resolve();

function mergeIntoMemory(seed: IDBSeedFile): void {
  for (const [storeName, entries] of Object.entries(seed.stores)) {
    if (!memorySeed[storeName]) memorySeed[storeName] = {};
    Object.assign(memorySeed[storeName], entries);
  }
}

function loadSeedShard(filename: string): Promise<void> {
  if (inFlight.has(filename)) return inFlight.get(filename)!;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    console.warn(`[SeedPrefetch] ${filename} — timeout after ${SEED_FETCH_TIMEOUT_MS}ms, aborting`);
    controller.abort();
  }, SEED_FETCH_TIMEOUT_MS);

  const promise = fetch(`/${filename}`, { signal: controller.signal })
    .then((r) => {
      return r.ok ? r.json() : null;
    })
    .then((seed: IDBSeedFile | null) => {
      if (!seed) {
        console.warn(`[SeedPrefetch] ${filename} — no seed`);
        return;
      }
      mergeIntoMemory(seed);
      return seedIDBFromStaticData(seed);
    })
    .catch((e) => { console.warn(`[SeedPrefetch] ${filename} FAILED`, e); })
    .finally(() => { clearTimeout(timeoutId); inFlight.delete(filename); }) as Promise<void>;

  inFlight.set(filename, promise);
  return promise;
}

/** Diagnostic: dump the in-memory seed keys (used by tests). */
export function debugMemorySeedKeys(): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const k of Object.keys(memorySeed)) out[k] = Object.keys(memorySeed[k]);
  return out;
}

/**
 * Test-only: clear all module-level state (in-flight fetches, in-memory seed
 * cache, cached year/make/model promises). Without this, a `loadSeedShard()`
 * call started by one test (e.g. via awaitModelsSeed) can still be in flight
 * — or worse, resolved-but-cached under a stale fetch mock's response — when
 * a later test in the same file awaits the same promise reference, since all
 * of this state is shared at module scope and vitest doesn't reload modules
 * between tests in one file. Call from an `afterEach` in tests that render
 * components exercising the year/make/model selection flow.
 */
export function __resetSeedPrefetchStateForTests(): void {
  inFlight.clear();
  for (const key of Object.keys(memorySeed)) delete memorySeed[key];
  _makesSeedPromise = Promise.resolve();
  _modelsSeedPromises.clear();
  _yearsSeedPromise = Promise.resolve();
}

/**
 * Load the years shard.
 * Call on app startup — needed immediately for the year dropdown.
 */
export function preloadYearsSeed(): void {
  _yearsSeedPromise = loadSeedShard("idb-seed-years.json");
}

/** Await this inside fetchYears to guarantee the years seed is in memory/IDB first. */
export function awaitYearsSeed(): Promise<void> {
  return _yearsSeedPromise;
}

/**
 * Load the makes shard (covers all years × all types).
 * Call on app startup in the background — will be ready before the user picks a year.
 */
export function preloadMakesSeed(): void {
  _makesSeedPromise = loadSeedShard("idb-seed-makes.json");
}

/** Await this inside fetchMakes to guarantee the makes seed is in memory/IDB first. */
export function awaitMakesSeed(): Promise<void> {
  return _makesSeedPromise;
}

/**
 * Load the models shard for a specific year.
 * Call as soon as the user selects a year so models are in IDB
 * before the make is chosen.
 */
export function preloadModelsForYear(year: string | number): void {
  if (!year) return;
  const key = String(year);
  const p = loadSeedShard(`idb-seed-models-${key}.json`);
  _modelsSeedPromises.set(key, p);
}

/** Await this inside fetchModels to guarantee the models seed for a year is in memory/IDB first. */
export function awaitModelsSeed(year: string | number): Promise<void> {
  if (!year) return Promise.resolve();
  const key = String(year);
  if (!_modelsSeedPromises.has(key)) {
    // First call may come from fetchModels itself if the user navigated
    // straight to a make before the year-change handler ran preloadModelsForYear.
    preloadModelsForYear(key);
  }
  return _modelsSeedPromises.get(key) ?? Promise.resolve();
}

/**
 * Direct in-memory lookup of a seeded entry. Returns `undefined` if the shard
 * for this store/key has not been loaded yet (or doesn't exist in the seed).
 *
 * Use this as a final fallback when IDB doesn't have the entry — e.g. when the
 * API is offline and IDB seeding raced with the user interaction.
 */
export function getSeedFromMemory<T = unknown>(store: string, key: string): T | undefined {
  return memorySeed[store]?.[key] as T | undefined;
}
